import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useRef } from "react";
import { brand, tocSections, stats, services, media } from "../data/mindscope";
import BrandLogo from "../components/mindscope/BrandLogo";
import { Reveal, RevealStagger, StaggerItem } from "../components/mindscope/Reveal";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <main data-testid="home-page" className="bg-ms-navy text-white overflow-hidden">
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative min-h-[100svh] flex items-center pt-28 pb-24 grain"
        style={{
          backgroundColor: "#0A1128",
          backgroundImage:
            "radial-gradient(ellipse at 15% 20%, rgba(26,50,91,0.9) 0%, transparent 55%), radial-gradient(circle at 88% 78%, rgba(201,169,97,0.12) 0%, transparent 45%)",
        }}
      >
        {/* Backdrop image */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <img
            src={media.heroBackdrop}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-[0.12] mix-blend-luminosity"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/60 via-[#0A1128]/40 to-[#0A1128]" />
        </div>

        {/* Floating logo watermark */}
        <div className="absolute right-4 md:right-10 top-24 md:top-32 opacity-[0.12] pointer-events-none hidden md:block float-slow">
          <BrandLogo variant="mark" className="h-64 w-auto" />
        </div>

        {/* Vertical gold accent line */}
        <motion.div
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="absolute left-6 lg:left-10 top-24 bottom-24 w-px bg-gradient-to-b from-transparent via-ms-gold to-transparent origin-top"
        />

        <motion.div style={{ y, opacity }} className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="eyebrow mb-10"
          >
            India&apos;s Trusted Capital Partner
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.4 }}
                className="font-display text-[15vw] md:text-[9rem] lg:text-[11rem] leading-[0.88] tracking-[-0.03em] text-white"
              >
                <span className="block overflow-hidden">
                  <motion.span
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="block"
                  >
                    Bold Vision.
                  </motion.span>
                </span>
                <span className="block overflow-hidden">
                  <motion.span
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="block italic text-ms-gold"
                  >
                    Brighter Future.
                  </motion.span>
                </span>
              </motion.h1>
            </div>

            <div className="lg:col-span-4 space-y-8">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.1 }}
                className="font-devanagari text-2xl md:text-3xl text-ms-gold leading-snug"
              >
                {brand.taglineSanskrit}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.25 }}
                className="font-display italic text-white/70 text-lg md:text-xl border-l border-ms-gold/40 pl-5"
              >
                {brand.taglineEnglish}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.4 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/services" className="btn-gold" data-testid="hero-cta-services">
                  Explore Solutions <ArrowUpRight size={16} />
                </Link>
                <Link to="/contact" className="btn-ghost" data-testid="hero-cta-contact">
                  Begin Dialogue
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-white/50"
        >
          <span className="text-[0.65rem] tracking-[0.35em] uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="w-px h-10 bg-white/40"
          />
        </motion.div>
      </section>

      {/* PERSPECTIVE */}
      <section data-testid="perspective-section" className="section-y bg-ms-navy relative">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <div className="eyebrow mb-6 justify-center" style={{ justifyContent: "center" }}>01 — The Perspective</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-white">
              Capital, curated with <span className="italic text-ms-gold">conscience</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 font-body text-lg md:text-xl text-white/75 leading-relaxed max-w-2xl mx-auto">
              Mindscope is India&apos;s trusted capital partner — a modern advisory collective that shepherds
              founders, boards and family enterprises through the most consequential moments of their
              capital journey.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <blockquote className="mt-12 max-w-3xl mx-auto">
              <p className="font-display italic text-2xl md:text-4xl text-white leading-snug">
                “We do not sell products. We architect outcomes — with the patience of a legacy house
                and the rigor of a modern practice.”
              </p>
            </blockquote>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-12 flex justify-center">
              <Link
                to="/about"
                data-testid="perspective-link"
                className="inline-flex items-center gap-3 font-body text-sm tracking-[0.24em] uppercase text-ms-gold hover:gap-5 transition-[gap] duration-500"
              >
                Read our identity <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS BAND */}
      <section data-testid="stats-band" className="bg-[#050914] border-y border-white/5 py-14">
        <RevealStagger className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-y-10 md:gap-y-0">
          {stats.map((s) => (
            <StaggerItem key={s.label} className="md:border-l md:border-white/10 md:px-8 first:md:border-l-0">
              <div className="tick-num text-6xl md:text-7xl text-ms-gold leading-none">{s.value}</div>
              <div className="mt-3 font-body text-sm text-white uppercase tracking-[0.18em]">{s.label}</div>
              <div className="mt-1 font-display italic text-white/50">{s.sub}</div>
            </StaggerItem>
          ))}
        </RevealStagger>
      </section>

      {/* CLIENTS + TESTIMONIALS now live at /clients */}

      {/* TABLE OF CONTENTS - editorial index */}
      <section data-testid="index-section" className="section-y bg-ms-paper text-ms-navy relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <Reveal className="lg:col-span-6">
              <div className="eyebrow !text-ms-navy mb-6" style={{ color: "#0A1128" }}>
                <span style={{ color: "#0A1128" }}>The Index</span>
              </div>
              <h2 className="font-display text-6xl md:text-7xl leading-[0.95] text-ms-navy">
                A firm read like a <span className="italic">chapter book</span>.
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.15}>
              <p className="font-body text-lg text-ms-navy/75 leading-relaxed">
                Six chapters — from perspective to practice. Each written with the discretion of a
                private memorandum and the clarity of a term sheet.
              </p>
            </Reveal>
          </div>

          <div className="border-t border-ms-navy/10">
            {tocSections.map((t, i) => (
              <Link
                key={t.num}
                to={t.route}
                data-testid={`toc-item-${t.num}`}
                className="group grid grid-cols-12 items-center py-8 md:py-10 border-b border-ms-navy/10 hover:bg-ms-navy/5 transition-colors duration-500 px-2 md:px-4"
              >
                <span className="col-span-2 md:col-span-1 font-display text-2xl md:text-3xl text-ms-gold-bright">{t.num}</span>
                <span className="col-span-8 md:col-span-9 font-display text-2xl md:text-4xl text-ms-navy group-hover:translate-x-3 transition-transform duration-500">
                  {t.title}
                </span>
                <span className="col-span-2 flex justify-end">
                  <ArrowUpRight size={28} className="text-ms-navy/40 group-hover:text-ms-gold-bright group-hover:rotate-12 transition-[transform,color] duration-500" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE PREVIEW */}
      <section className="section-y bg-ms-navy relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <Reveal className="lg:col-span-7">
              <div className="eyebrow mb-6">06 — Service Ecosystem</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-white">
                A full ecosystem, <span className="italic text-ms-gold">one mandate</span>.
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.15}>
              <p className="font-body text-white/70 leading-relaxed">
                From maiden IPOs to strategic delisting — a full-spectrum capital markets practice under
                a single roof.
              </p>
              <Link
                to="/services"
                data-testid="services-preview-cta"
                className="mt-6 inline-flex items-center gap-3 font-body text-sm tracking-[0.24em] uppercase text-ms-gold hover:gap-5 transition-[gap] duration-500"
              >
                See full ecosystem <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>

          <RevealStagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/5">
            {services.slice(0, 8).map((s) => (
              <StaggerItem
                key={s.num}
                className="bg-ms-navy p-6 md:p-8 group gold-border-hover !border-0 hover:bg-[#14264A] transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-display text-ms-gold text-lg">{s.num}</span>
                  <ArrowUpRight size={16} className="text-white/30 group-hover:text-ms-gold group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-500" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">{s.title}</h3>
                <div className="mt-3 text-[0.65rem] tracking-[0.3em] uppercase text-white/40">{s.tag}</div>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="section-y bg-[#050914] relative overflow-hidden">
        <img
          src={media.closing}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.08] mix-blend-luminosity pointer-events-none"
          loading="lazy"
        />
        <div className="absolute -right-40 -top-40 w-[500px] h-[500px] rounded-full bg-ms-gold/5 blur-3xl pointer-events-none" />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 text-center relative">
          <Reveal>
            <div className="flex justify-center mb-10">
              <BrandLogo variant="transparent" className="h-20 md:h-24 w-auto" />
            </div>
            <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.95]">
              A conversation is <span className="italic text-ms-gold">the first instrument</span>.
            </h2>
            <p className="mt-8 font-body text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
              If your enterprise is approaching a defining capital moment, we would be honoured to
              share a room, a whiteboard and an hour.
            </p>
            <div className="mt-10 flex justify-center gap-4 flex-wrap">
              <Link to="/contact" className="btn-gold" data-testid="closing-cta">
                Begin Dialogue <ArrowUpRight size={16} />
              </Link>
              <Link to="/leadership" className="btn-ghost">
                Meet the Firm
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
